import Style from './LeftBar.module.scss';
import { ReactComponent as EditIcon } from '../../../../assets/img/common/edit_20.svg';
import { ReactComponent as SubmitIcon } from '../../../../assets/img/formState/check_20.svg';
import { ReactComponent as RecallIcon } from '../../../../assets/img/common/reset_20.svg';
import { ReactComponent as SkippedIcon } from '../../../../assets/img/formState/cross_20.svg';
import { ReactComponent as ApproveIcon } from '../../../../assets/img/formState/double_check_20.svg';
import { ReactComponent as ApproveAfterSkippingIcon } from '../../../../assets/img/formState/double_cross_20.svg';
import { DateFormats, localFormat } from 'src/helpers/date-helpers';
import { FormChangesDto, FormChangingActionType } from 'src/services/api/api-client';
import { useTranslation } from 'react-i18next';
import { useGetFormUpdatingActionsName, getActorName, resolveChangingTime } from './historyHelper';
import { Dispatch, SetStateAction, useCallback, useEffect } from 'react';
import { SelectedVersion } from './HistoryTab';
import clsx from 'clsx';

export type CardProps = {
  currentVersion: FormChangesDto;
  hasEcrf?: boolean;
  setSelectedVersion: Dispatch<SetStateAction<FormChangesDto | undefined>>;
  selectedVersion: SelectedVersion;
};

export type ActorProps = {
  actorName: string;
  version?: string;
  formChangingType: FormChangingActionType;
};

const actionIcons = {
  [FormChangingActionType.Approve]: <ApproveIcon className={Style.icon} />,
  [FormChangingActionType.ChangeFields]: <EditIcon className={Style.icon} />,
  [FormChangingActionType.Recall]: <RecallIcon className={Style.icon} />,
  [FormChangingActionType.Skipped]: <SkippedIcon className={Style.icon} />,
  [FormChangingActionType.Submit]: <SubmitIcon className={Style.icon} />,
  [FormChangingActionType.ApprovedAfterSkipping]: <ApproveAfterSkippingIcon className={Style.icon} />,
  [FormChangingActionType.SubmissionRevoked]: <RecallIcon className={Style.icon} />,
};

export const ActionLine = ({ actorName, version, formChangingType }: ActorProps) => (
  <div className={Style.bottomLine}>
    {actionIcons[formChangingType]}
    <div className={Style.nameAndType}>
      <span className={Style.changerName}>{actorName}</span>
      <span>{useGetFormUpdatingActionsName(formChangingType)}</span>
    </div>
    <span className={Style.version}>{version}</span>
  </div>
);

export const Card = ({ currentVersion, hasEcrf, setSelectedVersion, selectedVersion }: CardProps) => {
  const { t } = useTranslation();

  const { versionNumber, formChangingActionType } = currentVersion;

  const majorVersion = hasEcrf ? t('Forms.HistoryOfChanges.Version', { version: versionNumber }) : '';
  const editTime = localFormat(resolveChangingTime(currentVersion) ?? currentVersion.updatedAt, DateFormats.dateTime);
  const updaterFullName = getActorName(currentVersion.formChangingActionType, currentVersion);

  const isSelected =
    selectedVersion.versionNumber === currentVersion.versionNumber &&
    selectedVersion.minorVersionNumber === currentVersion.minorVersionNumber &&
    selectedVersion.actionType === currentVersion.formChangingActionType;

  const handleCardClick = useCallback(() => {
    setSelectedVersion(currentVersion);
  }, [currentVersion, setSelectedVersion]);

  return (
    <div className={clsx(Style.card, isSelected ? Style.selected : '')} onClick={handleCardClick}>
      <div className={Style.topLine}>
        <span>{editTime}</span>
        <span>{majorVersion}</span>
      </div>
      <ActionLine actorName={updaterFullName!} formChangingType={formChangingActionType} />
    </div>
  );
};

export type LeftBarMenuProps = {
  versions: FormChangesDto[];
  hasEcrf?: boolean;
  setSelectedVersion: Dispatch<SetStateAction<FormChangesDto | undefined>>;
  selectedVersion: SelectedVersion;
};

export const HistoryLeftBar = ({ versions, hasEcrf, setSelectedVersion, selectedVersion }: LeftBarMenuProps) => {
  useEffect(() => {
    setSelectedVersion(versions[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versions.length, selectedVersion.formResultId]);

  return (
    <div className={Style.leftBarContainer}>
      {versions.map((version) => (
        <Card
          currentVersion={version}
          hasEcrf={hasEcrf}
          setSelectedVersion={setSelectedVersion}
          selectedVersion={selectedVersion}
          key={version.id}
        />
      ))}
    </div>
  );
};
